import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/Logo";
import { 
  Shield, 
  Crown, 
  Users,
  Eye,
  Loader2,
  CheckCircle2,
  XCircle,
  ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_INFO = {
  owner: {
    label: "Owner",
    description: "Full access, billing, can delete business",
    color: "bg-amber-100 text-amber-800",
    icon: Crown
  },
  admin: {
    label: "Admin",
    description: "Full access except billing",
    color: "bg-purple-100 text-purple-800",
    icon: Shield
  },
  manager: {
    label: "Manager",
    description: "Manage leads and forms",
    color: "bg-blue-100 text-blue-800",
    icon: Users
  },
  member: {
    label: "Member",
    description: "View-only access",
    color: "bg-gray-100 text-gray-800",
    icon: Eye
  }
};

export default function JoinTeamPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError("Invalid invitation link");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/team/invitation/${token}`);
        setInvitation(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Invitation not found or expired");
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/team/accept-invitation`, {
        token,
        name,
        password
      });
      
      // Store the token and redirect
      localStorage.setItem("heyintake_token", response.data.access_token);
      localStorage.setItem("token", response.data.access_token);
      
      setSuccess(true);
      toast.success("You've joined the team!");
      
      // Redirect to dashboard after a moment
      setTimeout(() => {
        navigate("/dashboard");
        window.location.reload(); // Refresh to update auth state
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const RoleBadge = ({ role }) => {
    const info = ROLE_INFO[role] || ROLE_INFO.member;
    const Icon = info.icon;
    return (
      <Badge className={`${info.color} flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="h-4 w-4" />
        {info.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center" style={{ fontFamily: 'Inter, sans-serif' }}>
        <Loader2 className="h-8 w-8 animate-spin text-[#1a4d2e]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
        <Card className="w-full max-w-md border-[#e5e5e0]">
          <CardContent className="pt-8 text-center">
            <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-[#1a1a1a] mb-2">Invitation Error</h2>
            <p className="text-[#666] mb-6">{error}</p>
            <Link to="/login">
              <Button className="bg-[#1a4d2e] hover:bg-[#2d6a4f]">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
        <Card className="w-full max-w-md border-[#e5e5e0]">
          <CardContent className="pt-8 text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-xl font-semibold text-[#1a1a1a] mb-2">Welcome to the Team!</h2>
            <p className="text-[#666] mb-2">You've successfully joined {invitation?.business_name}.</p>
            <p className="text-sm text-[#999]">Redirecting to dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Card className="w-full max-w-md border-[#e5e5e0]">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <Logo size={48} className="text-[#1a4d2e]" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1a4d2e]">
            Join {invitation?.business_name}
          </CardTitle>
          <CardDescription className="text-[#666]">
            You've been invited to join this team
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Invitation Details */}
          <div className="bg-[#f8f8f5] rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-[#666]">Invited as:</span>
              <RoleBadge role={invitation?.role} />
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-[#666]">Invited by:</span>
              <span className="text-sm font-medium">{invitation?.invited_by_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">Your email:</span>
              <span className="text-sm font-medium">{invitation?.email}</span>
            </div>
          </div>

          {/* Role Description */}
          <div className="mb-6 p-3 border border-[#e5e5e0] rounded-lg">
            <p className="text-sm text-[#666]">
              <strong>{ROLE_INFO[invitation?.role]?.label}</strong>: {ROLE_INFO[invitation?.role]?.description}
            </p>
          </div>

          {/* Accept Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium">Your Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1"
                data-testid="join-team-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-sm font-medium">Create Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                data-testid="join-team-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1"
                data-testid="join-team-confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-[#1a4d2e] hover:bg-[#2d6a4f]"
              data-testid="join-team-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Accept & Join Team
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-[#999] mt-6">
            By accepting, you agree to our{" "}
            <Link to="/terms" className="underline">Terms of Service</Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
